import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Play, X } from 'lucide-react';
import { missionContent, images } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

const Mission = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.mission-content',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handlePlayVideo = () => {
    setVideoOpen(true);
  };

  return (
    <section ref={sectionRef} className="py-32 bg-[#0B1121]">
      <div className="l-container">
        {/* Top Row */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12 mb-20 mission-content">
          <div className="lg:max-w-xl">
            <p className="sub-title">{missionContent.label}</p>
            <p className="site-description">{missionContent.text}</p>
          </div>
          <img
            src={images.missionGraphic}
            alt="Mission"
            className="w-64 lg:w-80 opacity-80"
          />
        </div>

        {/* Video Section - Only show if video URL exists */}
        {missionContent.videoUrl && (
        <div className="mission-content">
          <div className="flex items-center gap-4 mb-8">
            <svg className="w-8 h-8" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="18" stroke="white" strokeWidth="1" fill="none" opacity="0.3"/>
              <path d="M12 20C12 15.5817 15.5817 12 20 12C24.4183 12 28 15.5817 28 20" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
              <circle cx="20" cy="20" r="3" fill="white" opacity="0.3"/>
            </svg>
            <p className="text-white/50 text-sm">{missionContent.showreel}</p>
          </div>

          {/* Video Container */}
          <div
            className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group"
            onClick={handlePlayVideo}
          >
            <img
              src={images.videoPoster}
              alt="Video thumbnail"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
            
            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full border border-white/50 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={24} className="text-white ml-1" fill="white" />
                </div>
                <span className="site-description text-white">{missionContent.playText}</span>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Video Modal - Only show if video URL exists */}
      {videoOpen && missionContent.videoUrl && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          <button
            onClick={() => setVideoOpen(false)}
            className="absolute top-8 right-8 text-white hover:text-[#4ECDC4] transition-colors"
          >
            <X size={32} />
          </button>
          <div className="w-full max-w-6xl aspect-video mx-4">
            <video
              ref={videoRef}
              src={missionContent.videoUrl}
              className="w-full h-full"
              controls
              autoPlay
              playsInline
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Mission;
